import { useState, useEffect, useCallback } from 'react';
import type { TFunction } from 'i18next';

/**
 * Stripe v2 legacy API surface, accessed via the globally-injected `window.Stripe`.
 * Read through `stripeV2()` rather than augmenting `Window`, since @stripe/stripe-js
 * (loaded by useStripeV3) declares `window.Stripe` as the incompatible v3 constructor.
 */
interface StripeV2Global {
  setPublishableKey: (key: string) => void;
  card: {
    createToken: (form: HTMLFormElement, callback: (status: number, response: StripeV2TokenResponse) => void) => void;
  };
}

function stripeV2(): StripeV2Global | undefined {
  return (window as unknown as { Stripe?: StripeV2Global }).Stripe;
}

export interface StripeV2TokenResponse {
  id?: string;
  error?: {
    message: string;
    code?: string;
    type?: string;
    param?: string;
  };
  card?: {
    brand: string;
    funding?: string;
  };
}

/**
 * Map Stripe error codes to i18n keys under `booking:payment.stripe.*`.
 * Falls back to the raw Stripe message when no mapping exists.
 */
const STRIPE_ERROR_KEYS: Record<string, string> = {
  invalid_number: 'payment.stripe.invalidNumber',
  incorrect_number: 'payment.stripe.invalidNumber',
  invalid_expiry_month: 'payment.stripe.invalidExpiry',
  invalid_expiry_year: 'payment.stripe.invalidExpiry',
  invalid_cvc: 'payment.stripe.invalidCvc',
  incorrect_cvc: 'payment.stripe.invalidCvc',
  expired_card: 'payment.stripe.expiredCard',
  card_declined: 'payment.stripe.cardDeclined',
  processing_error: 'payment.stripe.processingError',
};

export function getStripeErrorMessage(
  error: { code?: string; message: string },
  t: TFunction,
): string {
  const i18nKey = error.code ? STRIPE_ERROR_KEYS[error.code] : undefined;
  // Try namespace-qualified key first (works from any namespace),
  // then unqualified (works when t is already in the booking namespace).
  if (i18nKey) {
    const qualified = t(`booking:${i18nKey}`, '');
    if (qualified) return qualified;
    const unqualified = t(i18nKey, '');
    if (unqualified) return unqualified;
  }
  return error.message || t('PAYMENT_CARD_ERROR');
}

export interface StripeTokenError extends Error {
  code?: string;
}

const STRIPE_V2_URL = 'https://js.stripe.com/v2/';
const STRIPE_V2_SCRIPT_ID = 'stripe-v2-sdk-script';
const POLL_INTERVAL = 250;
const TIMEOUT_MS = 15000;

interface UseStripeV2Result {
  isReady: boolean;
  error: string | null;
  createToken: (form: HTMLFormElement) => Promise<StripeV2TokenResponse>;
}

/**
 * CONSTRAINT: Stripe v2 uses a global `setPublishableKey`. Only one payment form
 * should be mounted at a time. `createToken` re-sets the key before each call
 * to handle key switches safely, but concurrent instances during the polling
 * phase could overwrite each other's key.
 */
export function useStripeV2(publishableKey: string): UseStripeV2Result {
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!publishableKey) return;

    setIsReady(false);
    setError(null);

    if (!document.getElementById(STRIPE_V2_SCRIPT_ID)) {
      const script = document.createElement('script');
      script.src = STRIPE_V2_URL;
      script.id = STRIPE_V2_SCRIPT_ID;
      script.async = true;
      document.body.appendChild(script);
    }

    const startTime = Date.now();
    const poll = setInterval(() => {
      const s = stripeV2();
      if (s && s.setPublishableKey) {
        clearInterval(poll);
        s.setPublishableKey(publishableKey);
        setIsReady(true);
      } else if (Date.now() - startTime > TIMEOUT_MS) {
        clearInterval(poll);
        setError('Stripe v2 SDK failed to load');
      }
    }, POLL_INTERVAL);

    return () => {
      clearInterval(poll);
      setIsReady(false);
      setError(null);
    };
  }, [publishableKey]);

  const createToken = useCallback((form: HTMLFormElement): Promise<StripeV2TokenResponse> => {
    return new Promise((resolve, reject) => {
      const s = stripeV2();
      if (!s) {
        reject(new Error('Stripe v2 SDK not loaded'));
        return;
      }
      // Re-set key before each tokenization (handles key switches)
      s.setPublishableKey(publishableKey);
      s.card.createToken(form, (_status, response) => {
        if (response.error) {
          const err = new Error(response.error.message);
          (err as StripeTokenError).code = response.error.code;
          reject(err);
        } else if (response.id) {
          resolve(response);
        } else {
          reject(new Error('Failed to create Stripe token'));
        }
      });
    });
  }, [publishableKey]);

  return { isReady, error, createToken };
}
